import React, { useState, useEffect } from "react";
import { X, Trophy, ArrowUp, ArrowDown, Trash2 } from "lucide-react";

const TopManageModal = ({ isOpen, onClose, torrent, onSuccess }) => {
  const [topItems, setTopItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [error, setError] = useState(null);

  const isAlreadyInTop = torrent?.is_top && torrent?.top_position;

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSelectedPosition(torrent?.top_position || null);
    fetchTopItems();
  }, [isOpen, torrent]);

  const fetchTopItems = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/top", {
        headers: {
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        setTopItems(data.items || []);
      }
    } catch (err) {
      console.error("Error fetching top items:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToTop = async () => {
    if (!selectedPosition) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/torrents/${torrent.id}/top`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
        body: JSON.stringify({ position: selectedPosition }),
      });
      const data = await res.json();
      if (res.ok) {
        onSuccess && onSuccess(data.items);
        onClose();
      } else {
        setError(data.error || "Failed to add to TOP");
      }
    } catch (err) {
      setError("Network error");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveFromTop = async (torrentId) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/torrents/${torrentId}/top`, {
        method: "DELETE",
        headers: {
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        setTopItems(data.items || []);
        onSuccess && onSuccess(data.items);
        // If we removed the current torrent, close
        if (String(torrentId) === String(torrent.id)) {
          onClose();
        }
      } else {
        setError(data.error || "Failed to remove from TOP");
      }
    } catch (err) {
      setError("Network error");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Build position slots 1-10
  const occupiedPositions = {};
  topItems.forEach((item) => {
    occupiedPositions[item.top_position] = item;
  });

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md mx-4 rounded-2xl border border-[#333] shadow-2xl overflow-hidden"
        style={{ backgroundColor: "#1a1a1c" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#333]">
          <div className="flex items-center gap-2">
            <Trophy size={20} className="text-yellow-500" />
            <h3 className="text-white font-semibold text-lg">
              {isAlreadyInTop ? "Manage TOP Position" : "Add to TOP-10"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Torrent info */}
        <div className="px-5 py-3 border-b border-[#282828]">
          <p className="text-sm text-white/80 truncate" title={torrent?.title}>
            {torrent?.title}
          </p>
        </div>

        {/* Content */}
        <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#282828] border-t-[#999]" />
            </div>
          ) : (
            <>
              {/* Position selector */}
              <p className="text-sm text-gray-400 mb-3">Select position:</p>
              <div className="grid grid-cols-5 gap-2 mb-4">
                {Array.from({ length: 10 }, (_, i) => i + 1).map((pos) => {
                  const occupant = occupiedPositions[pos];
                  const isCurrentTorrent =
                    occupant && String(occupant.id) === String(torrent.id);
                  const isOccupied = !!occupant && !isCurrentTorrent;
                  const isSelected = selectedPosition === pos;

                  return (
                    <button
                      key={pos}
                      onClick={() => setSelectedPosition(pos)}
                      className={`relative p-2 rounded-lg text-center text-sm font-medium transition-all duration-200 border ${
                        isSelected
                          ? "border-yellow-500 bg-yellow-500/20 text-yellow-400"
                          : isCurrentTorrent
                            ? "border-green-500/50 bg-green-500/10 text-green-400"
                            : isOccupied
                              ? "border-red-500/30 bg-red-500/5 text-red-400/70"
                              : "border-[#333] bg-[#232323] text-gray-300 hover:border-gray-500"
                      }`}
                      title={
                        occupant
                          ? `${occupant.title}${isCurrentTorrent ? " (current)" : ""}`
                          : `Position ${pos} — empty`
                      }
                    >
                      {pos}
                      {isOccupied && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-4">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-[#333] inline-block" />{" "}
                  Empty
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />{" "}
                  Occupied (will be swapped)
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />{" "}
                  Current
                </span>
              </div>

              {/* Current TOP items list */}
              {topItems.length > 0 && (
                <>
                  <p className="text-sm text-gray-400 mb-2">
                    Current TOP ({topItems.length}/10):
                  </p>
                  <div className="space-y-1.5 mb-4">
                    {topItems.map((item) => (
                      <div
                        key={item.id}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                          String(item.id) === String(torrent.id)
                            ? "border-yellow-500/30 bg-yellow-500/5"
                            : "border-[#333] bg-[#232323]"
                        }`}
                      >
                        <span className="text-yellow-500 font-bold text-sm w-6 text-center">
                          {item.top_position}
                        </span>
                        <span className="flex-1 text-sm text-white/80 truncate">
                          {item.title}
                        </span>
                        <button
                          onClick={() => handleRemoveFromTop(item.id)}
                          disabled={saving}
                          className="p-1 hover:bg-red-500/20 rounded transition-colors"
                          title="Remove from TOP"
                        >
                          <Trash2 size={14} className="text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Error */}
              {error && (
                <div className="text-red-400 text-sm mb-3 p-2 bg-red-500/10 rounded-lg border border-red-500/20">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-5 py-4 border-t border-[#333]">
          {isAlreadyInTop && (
            <button
              onClick={() => handleRemoveFromTop(torrent.id)}
              disabled={saving}
              className="flex-1 px-4 py-2.5 rounded-xl bg-red-600/80 hover:bg-red-600 disabled:bg-gray-700 text-white text-sm font-medium transition-colors"
            >
              Remove from TOP
            </button>
          )}
          <button
            onClick={handleAddToTop}
            disabled={saving || !selectedPosition}
            className="flex-1 px-4 py-2.5 rounded-xl bg-yellow-600/80 hover:bg-yellow-600 disabled:bg-gray-700 text-white text-sm font-medium transition-colors"
          >
            {saving
              ? "Saving..."
              : isAlreadyInTop
                ? "Update Position"
                : "Add to TOP"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopManageModal;
